import { Button, CloseButton, Group, Paper, Stack, Text } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { trpc } from '../../lib/trpc';
import { useSetupState } from './useSetupState';
import { stepsRemaining, type SetupState } from '../../lib/setup-state';

/**
 * The strip that brings a vendor back to setup after they skipped out of it.
 *
 * Renders nothing at all unless there is something to finish — no flag check
 * needed here, because `showBanner` is already false for a completed account, a
 * dismissed banner, and a platform-side blocker the vendor cannot act on. That
 * last one matters: nagging someone about a step that is impossible for them is
 * worse than staying quiet.
 *
 * Dismissal is persisted server-side rather than in localStorage, so dismissing
 * it on a laptop also dismisses it on a phone.
 */
const HEADLINE: Record<SetupState, string> = {
  BLOCKED_BY_PLATFORM: '',
  NOT_CONNECTED: 'Connect WhatsApp to start messaging',
  CONNECTING: 'Meta is still verifying your number',
  CANNOT_RECEIVE: "You're not receiving messages",
  UNPROVEN: 'Almost there — prove your connection works',
  LIVE: '',
};

const DETAIL: Record<SetupState, string> = {
  BLOCKED_BY_PLATFORM: '',
  NOT_CONNECTED: "Takes about two minutes. We'll walk you through it.",
  CONNECTING: 'Nothing to do — this updates as soon as Meta finishes.',
  CANNOT_RECEIVE: 'Messages people send you are being dropped. This one needs fixing.',
  UNPROVEN: 'Send yourself a test message to confirm both directions work.',
  LIVE: '',
};

export function SetupBanner() {
  const utils = trpc.useUtils();
  const setup = useSetupState();

  const dismiss = trpc.integrations.meta.onboarding.saveState.useMutation({
    onSuccess: () => void utils.integrations.meta.onboarding.getState.invalidate(),
  });

  const s = setup.snapshot;
  if (!s || !s.showBanner) return null;

  const remaining = stepsRemaining(s.state);
  const needsAttention = s.state === 'CANNOT_RECEIVE';

  return (
    <Paper
      withBorder
      radius="md"
      p="md"
      bg={needsAttention ? 'var(--mantine-color-orange-light)' : undefined}
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Stack gap={2}>
          <Text fw={600} size="sm">
            {HEADLINE[s.state]}
            {remaining > 0 && !needsAttention
              ? ` — ${remaining} step${remaining === 1 ? '' : 's'} left`
              : ''}
          </Text>
          <Text size="sm" c="dimmed">
            {DETAIL[s.state]}
          </Text>
        </Stack>
        <Group gap="xs" wrap="nowrap">
          <Button
            size="xs"
            component={Link}
            to="/vendor/setup"
            variant={needsAttention ? 'filled' : 'light'}
            color={needsAttention ? 'orange' : undefined}
          >
            {needsAttention ? 'Fix it' : 'Continue'}
          </Button>
          <CloseButton
            aria-label="Dismiss setup reminder"
            onClick={() => dismiss.mutate({ bannerDismissedAt: new Date().toISOString() })}
          />
        </Group>
      </Group>
    </Paper>
  );
}
